import { useState, useEffect, FormEvent } from 'react';
import {
  getCoaches,
  createCoach,
  updateCoach,
  deleteCoach,
  getTeams,
  COACH_TITLES,
  CoachMember,
  Team,
} from '../api/client';
import ImageUploader from '../components/ImageUploader';
import TeamNav from '../components/TeamNav';

/**
 * Coaches tab — a role-lens over Members (role=coach) with full CRUD.
 *
 * Coaches ARE Members. The backend keeps the team's `coaches` array
 * mirrored on every create/update/delete here, so the public website +
 * prerender (which read team.coaches) stay correct without any change.
 * A coach who is also a paying parent is ONE member — edit their other
 * roles on the Members tab.
 */

interface FormState {
  name: string;
  email: string;
  phone: string;
  image: string;
  coachTitle: string;
  showEmailPublicly: boolean;
  teamIds: string[];
}

function emptyForm(): FormState {
  return { name: '', email: '', phone: '', image: '', coachTitle: 'Coach', showEmailPublicly: false, teamIds: [] };
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0]![0]! + (parts[1]?.[0] || '')).toUpperCase();
}

export default function Coaches() {
  const [coaches, setCoaches] = useState<CoachMember[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<CoachMember | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [c, t] = await Promise.all([getCoaches(), getTeams()]);
      setCoaches(c);
      setTeams(t);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load coaches');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setShowModal(true);
  }

  function openEdit(c: CoachMember) {
    setEditing(c);
    setForm({
      name: c.name,
      email: c.email,
      phone: c.phone,
      image: c.image,
      coachTitle: c.coachTitle || 'Coach',
      showEmailPublicly: !!c.showEmailPublicly,
      teamIds: c.teams.map(t => t.teamId),
    });
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditing(null);
    setForm(emptyForm());
  }

  function toggleTeam(teamId: string) {
    setForm(f => ({
      ...f,
      teamIds: f.teamIds.includes(teamId) ? f.teamIds.filter(t => t !== teamId) : [...f.teamIds, teamId],
    }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (uploadingPhoto) { setError('Wait for the photo upload to finish before saving'); return; }
    if (!form.name.trim()) { setError('Coach name is required'); return; }
    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        image: form.image,
        coachTitle: form.coachTitle,
        showEmailPublicly: form.showEmailPublicly,
        teamIds: form.teamIds,
      };
      if (editing) {
        await updateCoach(editing.memberId, payload);
        setSuccess(`Updated ${form.name.trim()}`);
      } else {
        await createCoach(payload);
        setSuccess(`Added ${form.name.trim()}`);
      }
      await load();
      closeModal();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save coach');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(c: CoachMember) {
    if (!confirm(`Remove ${c.name} as a coach? They'll be unassigned from all teams. If they're also a parent/payer, that stays — only the coach role is removed.`)) return;
    try {
      await deleteCoach(c.memberId);
      setSuccess(`${c.name} removed as coach`);
      await load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove coach');
    }
  }

  const teamName = (id: string) => teams.find(t => t.id === id)?.name || '';

  return (
    <div className="tm-page-shell">
      <TeamNav />

      <div className="page-head">
        <div>
          <h1>Coaches</h1>
          <p className="subtitle">
            Everyone coaching across your teams. Coaches appear on the public team
            pages. Assigning a coach to a team here updates the website automatically.
          </p>
        </div>
        <div className="head-actions">
          <button className="ebtn ebtn-primary" onClick={openCreate} disabled={teams.length === 0}>
            + Add coach
          </button>
        </div>
      </div>

      {error && <div className="ealert ealert-error">{error}</div>}
      {success && <div className="ealert ealert-success">{success}</div>}
      {teams.length === 0 && (
        <div className="ealert ealert-info">
          Add a team first (Teams tab) — coaches are assigned to teams.
        </div>
      )}

      {loading ? (
        <p className="loading">Loading coaches…</p>
      ) : coaches.length === 0 ? (
        <div className="empty-state">
          <p>No coaches yet.</p>
          {teams.length > 0 && (
            <button className="ebtn ebtn-primary" style={{ marginTop: 12 }} onClick={openCreate}>+ Add your first coach</button>
          )}
        </div>
      ) : (
        <div className="card-surface">
          <table className="tm-table">
            <thead>
              <tr>
                <th>Coach</th>
                <th>Team(s)</th>
                <th>Email</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {coaches.map(c => (
                <tr key={c.memberId}>
                  <td>
                    <div className="tm-person">
                      {c.image ? (
                        <img className="tm-avatar" src={c.image} alt="" />
                      ) : (
                        <span className="tm-avatar tm-avatar-empty">{initials(c.name)}</span>
                      )}
                      <span className="tm-person-name">{c.name}</span>
                    </div>
                  </td>
                  <td>
                    <div className="tm-chips">
                      {c.teams.length === 0
                        ? <span className="tm-muted">Unassigned</span>
                        : c.teams.map(t => <span key={t.teamId} className="tm-chip">{t.name || teamName(t.teamId)}</span>)}
                    </div>
                  </td>
                  <td>{c.email ? <a className="tm-link" href={`mailto:${c.email}`}>{c.email}</a> : <span className="tm-muted">—</span>}</td>
                  <td>
                    <div className="tm-actions">
                      <button className="ebtn ebtn-secondary ebtn-sm" onClick={() => openEdit(c)}>Edit</button>
                      <button className="ebtn ebtn-danger ebtn-sm" onClick={() => handleRemove(c)}>Remove</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="tm-modal-overlay" onClick={closeModal}>
          <div className="tm-modal" onClick={e => e.stopPropagation()}>
            <div className="tm-modal-header">
              <h2>{editing ? 'Edit coach' : 'Add coach'}</h2>
              <button type="button" className="tm-modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="tm-modal-body">
                <label className="tm-field">
                  <span>Name *</span>
                  <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                </label>
                <div className="tm-form-row">
                  <label className="tm-field">
                    <span>Email</span>
                    <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                  </label>
                  <label className="tm-field">
                    <span>Phone</span>
                    <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                  </label>
                </div>
                <label
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0 4px', fontSize: '0.9rem',
                    cursor: form.email.trim() ? 'pointer' : 'not-allowed', opacity: form.email.trim() ? 1 : 0.55,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.showEmailPublicly}
                    disabled={!form.email.trim()}
                    onChange={e => setForm({ ...form, showEmailPublicly: e.target.checked })}
                    style={{ width: 'auto', margin: 0 }}
                  />
                  Show this email on the public team page
                </label>
                <label className="tm-field">
                  <span>Role (shown on the public team page)</span>
                  <select value={form.coachTitle} onChange={e => setForm({ ...form, coachTitle: e.target.value })}>
                    {COACH_TITLES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <div className="tm-field">
                  <span>Photo (shown on the public team page)</span>
                  <ImageUploader
                    currentImage={form.image || undefined}
                    onUpload={url => setForm(f => ({ ...f, image: url }))}
                    onUploadingChange={setUploadingPhoto}
                    label="Upload photo"
                  />
                </div>
                <div className="tm-field">
                  <span>Teams</span>
                  {teams.length === 0 ? (
                    <small>No teams yet.</small>
                  ) : (
                    <div className="tm-roles">
                      {teams.map(t => (
                        <label key={t.id}>
                          <input type="checkbox" checked={form.teamIds.includes(t.id)} onChange={() => toggleTeam(t.id)} />
                          {t.name}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="tm-modal-actions">
                <button type="button" className="ebtn ebtn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="ebtn ebtn-primary" disabled={saving || uploadingPhoto}>
                  {saving ? 'Saving…' : editing ? 'Save changes' : 'Add coach'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
